import React from 'react';
import { TrendingUp, Calendar } from 'lucide-react';

export default function DemandForecastingChart({ data }) {
  if (!data || data?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Prévision de Demande</h3>
        <div className="text-center py-8 text-gray-500">
          <Calendar size={48} className="mx-auto mb-2 text-gray-300" />
          <p>Aucune donnée disponible</p>
        </div>
      </div>
    );
  }

  const maxValue = Math.max(...data?.map(d => d?.total));
  const totalDemand = data?.reduce((sum, d) => sum + d?.total, 0);
  const avgDailyDemand = totalDemand / data?.length;
  const peakDay = data?.reduce((max, d) => d?.total > max?.total ? d : max, data?.[0]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Prévision de Demande</h3>
        <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-lg">
          <TrendingUp size={16} className="text-blue-600" />
          <span className="text-sm font-medium text-blue-600">Tendance hebdomadaire</span>
        </div>
      </div>

      {/* Chart */}
      <div className="space-y-3 mb-4">
        {data?.map((item, index) => {
          const isWeekend = item?.day === 'Sam' || item?.day === 'Dim';
          const isPeak = item?.day === peakDay?.day;
          
          return (
            <div key={index} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${isPeak ? 'text-green-600' : 'text-gray-900'}`}>
                    {item?.day}
                  </span>
                  {isPeak && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      Pic
                    </span>
                  )}
                  {isWeekend && (
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                      Weekend
                    </span>
                  )}
                </div>
                <span className="font-medium text-gray-900">{item?.total} unités</span>
              </div>
              <div className="relative h-6 bg-gray-100 rounded-lg overflow-hidden">
                <div 
                  className={`absolute left-0 top-0 h-full transition-all ${
                    isPeak ? 'bg-gradient-to-r from-green-500 to-green-400' : isWeekend ?'bg-gradient-to-r from-gray-400 to-gray-300': 'bg-gradient-to-r from-blue-500 to-blue-400'
                  }`}
                  style={{ width: `${(item?.total / maxValue) * 100}%` }}
                >
                  {item?.total > 0 && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                      <span className="text-xs text-white font-medium">
                        N:{item?.noir} B:{item?.bleu}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
        <div>
          <p className="text-xs text-gray-600 mb-1">Demande Moyenne/Jour</p>
          <p className="text-lg font-bold text-gray-900">{avgDailyDemand?.toFixed(1)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-600 mb-1">Jour de Pic</p>
          <p className="text-lg font-bold text-green-600">{peakDay?.day} ({peakDay?.total})</p>
        </div>
      </div>

      {/* Forecast Info */}
      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
        <p className="text-xs text-blue-800">
          <span className="font-medium">Prévision:</span> Demande estimée pour la semaine prochaine: {(avgDailyDemand * 7)?.toFixed(0)} unités
        </p>
      </div>
    </div>
  );
}